import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { pageButtonStyle } from './pagination';

// Reads the ordered id sequence a list handed over via navigation state
// when it sent you to one of its rows' detail pages (see BrowseNav below).
// Returns undefined if the page wasn't reached that way.
export function useBrowseIds() {
  const location = useLocation();
  return location.state?.browseIds;
}

// Generic "step through the list you came from" control for any detail
// page reached from a list's kebab menu — lets you page through the same
// filtered/sorted set of rows the list was showing (its ids handed over as
// navigation state, see useBrowseIds/ProductTable.js) without returning to
// the list and losing your place there. `buildPath` maps a row id to that
// page's own URL, so this component stays unaware of any specific route
// and is reusable across modules (Products, Price Adjustments, ...).
// Renders nothing if there's no known sequence to browse — direct URL,
// refresh, or an entry point that didn't hand one over — and nothing
// beyond the ids the list actually handed over (its current page, if the
// list paginates).
// `extraState` (optional): merged into each step's navigation state
// alongside browseIds — e.g. CommercialEntityDetailPage.js passes its own
// `origin` through so Prev/Next keeps the breadcrumb pointing at the list
// you actually came from, not just the first record you landed on.
function BrowseNav({ id, ids, buildPath, noun, extraState }) {
  const navigate = useNavigate();

  if (!Array.isArray(ids) || ids.length === 0) return null;
  const index = ids.findIndex((browseId) => String(browseId) === String(id));
  if (index === -1) return null;
  const atFirst = index === 0;
  const atLast = index === ids.length - 1;

  const goTo = (targetId) => navigate(buildPath(targetId), { state: { browseIds: ids, ...extraState } });

  // Same button treatment as the list's own Pagination footer
  // (pagination.js) — reads as "the control you'd expect here" rather than
  // a stray row of text links, since it's doing the same job (stepping
  // through an ordered set) one level down, on a single record. Not a list
  // itself, but still a footer — the top ruler is the same weight/color as
  // Pagination's own (#b3bbc7, 1px), just as a border-top instead of
  // border-bottom, since here it's the only thing on the row.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #b3bbc7' }}>
      <button type="button" onClick={() => goTo(ids[index - 1])} disabled={atFirst} style={pageButtonStyle(atFirst)}>Prev</button>
      <span style={{ fontSize: 13, color: '#555' }}>{index + 1} of {ids.length}{noun ? ` ${noun}` : ''}</span>
      <button type="button" onClick={() => goTo(ids[index + 1])} disabled={atLast} style={pageButtonStyle(atLast)}>Next</button>
    </div>
  );
}

export default BrowseNav;
